import { useEffect, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ClipboardList, Calendar, AlertCircle } from 'lucide-react';
import { parseCellValue } from '../utils/parseCellData';

export function AssignmentsReviewStep({
  sheetData,
  mapping,
  userMatches,
  qualMatches,
  assignmentConfig,
  setAssignmentConfig,
  parsedAssignments,
  setParsedAssignments
}) {
  const config = assignmentConfig || {
    dateFormat: 'DD/MM/YYYY',
    dateInterpretation: 'auto',
  };

  // Parse assignments whenever config changes
  useEffect(() => {
    if (!sheetData || !mapping || userMatches.length === 0 || qualMatches.length === 0) return;

    const assignments = [];

    for (const user of userMatches) {
      if (user.action === 'skip') continue;

      for (const qual of qualMatches) {
        if (qual.action === 'skip') continue;

        const row = sheetData[user.rowIndex];
        if (!row) continue;

        const rawValue = row[qual.colIndex];
        if (rawValue === undefined || rawValue === null || rawValue === '') continue;

        const parsed = parseCellValue(rawValue, {
          dateFormat: config.dateFormat,
          dateInterpretation: config.dateInterpretation,
        });

        // Skip cells that don't represent real assignments
        // (empty, not required, "/", "NA", etc.)
        if (parsed.skip) continue;

        assignments.push({
          rowIndex: user.rowIndex,
          colIndex: qual.colIndex,
          userName: user.rawName,
          qualName: qual.rawName,
          rawValue: String(rawValue),
          ...parsed
        });
      }
    }

    setParsedAssignments(assignments);
  }, [sheetData, mapping, userMatches, qualMatches, config.dateFormat, config.dateInterpretation, setParsedAssignments]);

  const stats = useMemo(() => {
    const total = parsedAssignments.length;
    const withDates = parsedAssignments.filter(a => a.completedDate || a.expiryDate).length;
    const withStatus = parsedAssignments.filter(a => a.status && a.status !== 'pending').length;
    const withCert = parsedAssignments.filter(a => a.certificateNumber).length;
    const unparsed = parsedAssignments.filter(a => !a.parsed).length;
    return { total, withDates, withStatus, withCert, unparsed };
  }, [parsedAssignments]);

  // Sample of parsed assignments for preview
  const sampleAssignments = parsedAssignments.slice(0, 25);

  return (
    <div className="space-y-4">
      {/* Config panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4 space-y-2">
            <Label className="text-xs font-semibold">Date Format</Label>
            <Select
              value={config.dateFormat}
              onValueChange={(val) => setAssignmentConfig({ ...config, dateFormat: val })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (UK)</SelectItem>
                <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (US)</SelectItem>
                <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (ISO)</SelectItem>
                <SelectItem value="auto">Auto-detect</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-2">
            <Label className="text-xs font-semibold">Dates Represent</Label>
            <Select
              value={config.dateInterpretation}
              onValueChange={(val) => setAssignmentConfig({ ...config, dateInterpretation: val })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (future = expiry, past = completion)</SelectItem>
                <SelectItem value="completion">Always Completion Date</SelectItem>
                <SelectItem value="expiry">Always Expiry Date</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold">Summary</p>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.total} assignments found
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-3 flex-wrap">
        <Badge variant="outline" className="gap-1.5 py-1">
          <ClipboardList className="h-3.5 w-3.5 text-blue-600" />
          {stats.total} total
        </Badge>
        <Badge variant="outline" className="gap-1.5 py-1">
          <Calendar className="h-3.5 w-3.5 text-green-600" />
          {stats.withDates} with dates
        </Badge>
        {stats.withCert > 0 && (
          <Badge variant="outline" className="gap-1.5 py-1">
            {stats.withCert} with cert #
          </Badge>
        )}
        {stats.unparsed > 0 && (
          <Badge variant="outline" className="gap-1.5 py-1 border-amber-300">
            <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
            {stats.unparsed} unrecognized
          </Badge>
        )}
      </div>

      {/* Sample table */}
      <ScrollArea className="h-[350px] border rounded-lg">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr className="border-b">
              <th className="text-left px-3 py-2 font-medium">User</th>
              <th className="text-left px-3 py-2 font-medium">Qualification</th>
              <th className="text-left px-3 py-2 font-medium">Raw Value</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Date</th>
              <th className="text-left px-3 py-2 font-medium">Cert #</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sampleAssignments.map((a, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-3 py-1.5 truncate max-w-[140px]">{a.userName}</td>
                <td className="px-3 py-1.5 truncate max-w-[140px]">{a.qualName}</td>
                <td className="px-3 py-1.5 max-w-[120px]">
                  <code className="text-[10px] bg-gray-100 px-1 py-0.5 rounded truncate block">
                    {a.rawValue}
                  </code>
                </td>
                <td className="px-3 py-1.5">
                  <StatusBadge status={a.status} parsed={a.parsed} />
                </td>
                <td className="px-3 py-1.5 text-muted-foreground">
                  {a.completedDate || a.expiryDate || '-'}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground">
                  {a.certificateNumber || '-'}
                </td>
              </tr>
            ))}
            {sampleAssignments.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-muted-foreground">
                  No assignments found. Check your mapping configuration.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </ScrollArea>
      {parsedAssignments.length > 25 && (
        <p className="text-xs text-muted-foreground text-center">
          Showing 25 of {parsedAssignments.length} assignments
        </p>
      )}
    </div>
  );
}

function StatusBadge({ status, parsed }) {
  if (!parsed) {
    return <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Unknown</Badge>;
  }
  const styles = {
    completed: 'bg-green-100 text-green-800',
    pending: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-blue-100 text-blue-800',
    expired: 'bg-red-100 text-red-800',
    not_required: 'bg-gray-100 text-gray-500',
  };
  const labels = {
    completed: 'Completed',
    pending: 'Pending',
    in_progress: 'In Progress',
    expired: 'Expired',
    not_required: 'N/A',
  };
  return (
    <Badge className={`text-[10px] ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </Badge>
  );
}
